import { useEffect, useRef } from 'react';

const DEFAULT_INTERVAL_MS = 1000 / 30; // ~30fps

/**
 * useRafThrottle — requestAnimationFrame 루프를 돌며 `onFrame`을 최대 ~30fps로만 호출한다.
 *
 * 고빈도 소스(예: ~47fps pitch_frame)를 매번 setState 하지 않기 위한 "#9 패턴": 최신 값은
 * 호출부가 ref에 모으고, 이 훅은 rAF tick마다 간격을 넘겼을 때만 `onFrame`(대개 `setX(ref.current)`)
 * 을 부른다. `onFrame`은 ref로 잡아 두므로 매 렌더마다 새 클로저를 넘겨도 effect는 한 번만 돈다.
 * 언마운트 시 rAF를 취소한다.
 */
export const useRafThrottle = (
  onFrame: () => void,
  intervalMs: number = DEFAULT_INTERVAL_MS,
): void => {
  const onFrameRef = useRef(onFrame);
  onFrameRef.current = onFrame;

  useEffect(() => {
    let raf = 0;
    let lastTs = 0;
    const tick = (ts: number) => {
      raf = requestAnimationFrame(tick);
      if (ts - lastTs < intervalMs) {
        return;
      }
      lastTs = ts;
      onFrameRef.current();
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [intervalMs]);
};
